/**
 * TagBadge — chip visual de tag com cor + ícone + nome (FEAT-V2-031).
 *
 * Reusável em DealCard, OSCard, TagPicker, TagFilterChip, modal de detalhe.
 *
 * A cor `tag.cor` é hex da paleta canônica (server/lib/tags.ts TAG_COLORS_PALETTE).
 * Style inline necessário porque Tailwind não pode pré-compilar classes
 * dinâmicas (`bg-[#XXXXXX]` exigiria safelist enorme).
 */

import type { Tag } from '@/lib/tags-store';

interface TagBadgeProps {
  tag: Pick<Tag, 'id' | 'nome' | 'cor' | 'icone' | 'ativo'>;
  size?: 'sm' | 'md';
  removable?: boolean;
  onRemove?: () => void;
  /** Click handler (não-removable) — usado em filtros. */
  onClick?: () => void;
  /** Estado visual selecionado (filtros) — borda mais grossa. */
  selected?: boolean;
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'text-xs px-1.5 py-0.5 gap-1',
  md: 'text-xs px-2 py-1 gap-1.5',
} as const;

/**
 * Calcula cor de texto contrastante (claro/escuro) dado um hex da paleta.
 * Implementação simples: hex → relative luminance → escolhe preto/branco.
 */
function pickTextColor(hexCor: string): string {
  const hex = hexCor.replace('#', '');
  if (hex.length !== 6) return '#ffffff';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // luminance approx (perceptual)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1f2937' : '#ffffff'; // gray-800 ou white
}

export function TagBadge({
  tag,
  size = 'md',
  removable = false,
  onRemove,
  onClick,
  selected = false,
  className = '',
}: TagBadgeProps) {
  const textColor = pickTextColor(tag.cor);
  const interactive = !!onClick || removable;
  const Component = onClick ? 'button' : 'span';

  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={`tag-badge-${tag.id}`}
      data-tag-active={tag.ativo ? 'true' : 'false'}
      title={tag.ativo ? tag.nome : `${tag.nome} (inativa)`}
      style={{
        backgroundColor: tag.cor,
        color: textColor,
        ...(selected ? { outline: '2px solid currentColor', outlineOffset: 2 } : {}),
      }}
      className={`inline-flex items-center rounded-full font-medium leading-none ${
        SIZE_CLASSES[size]
      } ${interactive ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''} ${
        !tag.ativo ? 'opacity-60 line-through' : ''
      } ${className}`}
    >
      {tag.icone && (
        <span aria-hidden className="flex-shrink-0">
          {tag.icone}
        </span>
      )}
      <span className="truncate max-w-[12rem]">{tag.nome}</span>
      {removable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          aria-label={`Remover tag ${tag.nome}`}
          data-testid={`tag-badge-remove-${tag.id}`}
          className="ml-0.5 hover:opacity-70 flex-shrink-0"
          style={{ color: textColor }}
        >
          ✕
        </button>
      )}
    </Component>
  );
}

/**
 * Renderiza N badges em linha, com chip "+X" quando excede maxVisible.
 * Tooltip do "+X" lista nomes das tags ocultas.
 */
export function TagBadgeList({
  tags,
  maxVisible = 3,
  size = 'sm',
  className = '',
}: {
  tags: Array<Pick<Tag, 'id' | 'nome' | 'cor' | 'icone' | 'ativo'>>;
  maxVisible?: number;
  size?: 'sm' | 'md';
  className?: string;
}) {
  if (tags.length === 0) return null;
  const visible = tags.slice(0, maxVisible);
  const hidden = tags.slice(maxVisible);
  return (
    <div
      className={`flex flex-wrap items-center gap-1 ${className}`}
      data-testid="tag-badge-list"
    >
      {visible.map((t) => (
        <TagBadge key={t.id} tag={t} size={size} />
      ))}
      {hidden.length > 0 && (
        <span
          data-testid="tag-badge-list-overflow"
          title={hidden.map((t) => t.nome).join(', ')}
          className={`inline-flex items-center rounded-full font-medium leading-none bg-paper-warm dark:bg-paper-dark-warm text-ink-2 dark:text-ink-dark-2 ${SIZE_CLASSES[size]}`}
        >
          +{hidden.length}
        </span>
      )}
    </div>
  );
}
